import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Send the httpOnly auth cookie with every request to the API.
axios.defaults.withCredentials = true;

// Helper kept for backward-compatibility with call sites; cookies now carry
// the user JWT, so this returns no headers for normal requests.
const authHeaders = () => ({});

// Stock API
export const stockAPI = {
  search: async (query) => {
    const response = await axios.get(`${API_BASE}/stocks/search`, { params: { query }, headers: authHeaders() });
    return response.data;
  },

  getQuote: async (symbol) => {
    const response = await axios.get(`${API_BASE}/stocks/${symbol}/quote`, { headers: authHeaders() });
    return response.data;
  },

  getAnalysts: async (symbol) => {
    const response = await axios.get(`${API_BASE}/stocks/${symbol}/analysts`, { headers: authHeaders() });
    return response.data;
  },

  getHistory: async (symbol, period = '3mo') => {
    const response = await axios.get(`${API_BASE}/stocks/${symbol}/history`, { params: { period }, headers: authHeaders() });
    return response.data;
  },

  getOptionsFlow: async (symbol, marketCap) => {
    const response = await axios.get(`${API_BASE}/stocks/${symbol}/options-flow`, {
      params: { market_cap: marketCap },
      headers: authHeaders(),
    });
    return response.data;
  },

  getAIChart: async (symbol, period = '6mo', commentary = true) => {
    const response = await axios.get(`${API_BASE}/stocks/${symbol}/ai-chart`, {
      params: { period, commentary },
      headers: authHeaders(),
    });
    return response.data;
  },
};

// Screener API
export const screenerAPI = {
  run: async (params = {}) => {
    const response = await axios.get(`${API_BASE}/screener/run`, {
      params,
      headers: authHeaders(),
    });
    return response.data;
  },
};

// Analysis API
export const analysisAPI = {
  getSignals: async (symbol, timeframe = 'both') => {
    const response = await axios.post(
      `${API_BASE}/analysis/${symbol}/signals`,
      { timeframe },
      { headers: { 'Content-Type': 'application/json', ...authHeaders() } },
    );
    return response.data;
  },

  getSuggestions: async (symbol, params = {}) => {
    const response = await axios.post(`${API_BASE}/analysis/${symbol}/suggestions`, params, {
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
    });
    return response.data;
  },
};

// Strategy API
export const strategyAPI = {
  match: async (symbol) => {
    const response = await axios.get(`${API_BASE}/strategies/match`, { params: { symbol }, headers: authHeaders() });
    return response.data;
  },

  getAll: async () => {
    const response = await axios.get(`${API_BASE}/strategies/all`, { headers: authHeaders() });
    return response.data;
  },
};

// Daily Picks API
export const picksAPI = {
  getDaily: async () => {
    const response = await axios.get(`${API_BASE}/picks/daily`, { headers: authHeaders() });
    return response.data;
  },
};

// Trending API
export const trendingAPI = {
  getStocks: async (limit = 10) => {
    const response = await axios.get(`${API_BASE}/trending/stocks`, {
      params: { limit },
      headers: authHeaders(),
    });
    return response.data;
  },
  getPopular: async (limit = 5) => {
    const response = await axios.get(`${API_BASE}/trending/popular`, {
      params: { limit },
      headers: authHeaders(),
    });
    return response.data;
  },
  getGlobalTrending: async (limit = 10) => {
    const response = await axios.get(`${API_BASE}/search/global-trending`, {
      params: { limit },
      headers: authHeaders(),
    });
    return response.data;
  },
};

// Search History API
export const searchHistoryAPI = {
  save: async (stock) => {
    const response = await axios.post(
      `${API_BASE}/search/history`,
      null,
      {
        params: {
          symbol: stock.symbol,
          name: stock.name,
          price: stock.price,
          change: stock.change,
          changePercent: stock.changePercent,
        },
        headers: authHeaders(),
      },
    );
    return response.data;
  },
  list: async (limit = 10) => {
    const response = await axios.get(`${API_BASE}/search/history`, {
      params: { limit },
      headers: authHeaders(),
    });
    return response.data;
  },
  remove: async (id) => {
    const response = await axios.delete(`${API_BASE}/search/history/${id}`, {
      headers: authHeaders(),
    });
    return response.data;
  },
  clear: async () => {
    const response = await axios.delete(`${API_BASE}/search/history`, {
      headers: authHeaders(),
    });
    return response.data;
  },
};

// Usage / limits
export const usageAPI = {
  registerSearch: async () => {
    const response = await axios.post(
      `${API_BASE}/usage/search-start`,
      {},
      { headers: { 'Content-Type': 'application/json', ...authHeaders() } },
    );
    return response.data;
  },
};

// Journal API (Pro only) - Enhanced with institutional trading features
export const journalAPI = {
  list: async (status = null) => {
    const params = status ? { status } : {};
    const response = await axios.get(`${API_BASE}/journal`, { 
      params,
      headers: authHeaders() 
    });
    return response.data;
  },
  getStats: async () => {
    const response = await axios.get(`${API_BASE}/journal/stats`, { headers: authHeaders() });
    return response.data;
  },
  create: async (payload) => {
    const response = await axios.post(`${API_BASE}/journal`, payload, {
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
    });
    return response.data;
  },
  createFromSuggestion: async (symbol, suggestionType, entryPrice, size = 1, thesis = null) => {
    const response = await axios.post(
      `${API_BASE}/journal/from-suggestion`,
      null,
      {
        params: { symbol, suggestion_type: suggestionType, entry_price: entryPrice, size, thesis },
        headers: authHeaders(),
      }
    );
    return response.data;
  },
  update: async (id, payload) => {
    const response = await axios.put(`${API_BASE}/journal/${id}`, payload, {
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
    });
    return response.data;
  },
  close: async (id, payload) => {
    const response = await axios.post(
      `${API_BASE}/journal/${id}/close`,
      payload,
      { headers: { 'Content-Type': 'application/json', ...authHeaders() } },
    );
    return response.data;
  },
  remove: async (id) => {
    const response = await axios.delete(`${API_BASE}/journal/${id}`, { headers: authHeaders() });
    return response.data;
  },
};

// Admin API - now uses httpOnly admin_token cookie set by /admin/login.
// withCredentials=true is set globally in App.js + api.js so requests carry it.
export const adminAPI = {
  me: async () => {
    const response = await axios.get(`${API_BASE}/admin/me`);
    return response.data;
  },
  login: async (password) => {
    const response = await axios.post(
      `${API_BASE}/admin/login`,
      { password },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return response.data;
  },
  logout: async () => {
    const response = await axios.post(`${API_BASE}/admin/logout`);
    return response.data;
  },
  searchUser: async (email) => {
    const response = await axios.get(`${API_BASE}/admin/users/search`, {
      params: { email },
    });
    return response.data;
  },
  listUsers: async (limit = 50, skip = 0) => {
    const response = await axios.get(`${API_BASE}/admin/users`, {
      params: { limit, skip },
    });
    return response.data;
  },
  toggleTier: async (email, tier) => {
    const response = await axios.post(
      `${API_BASE}/admin/users/toggle-tier`,
      { email, tier },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return response.data;
  },

  // Pending Pro-upgrade requests (Income Blueprint purchases)
  listUpgradeRequests: async (status = 'pending') =>
    (await axios.get(`${API_BASE}/admin/upgrade-requests`, { params: { status } })).data,
  approveUpgradeRequest: async (id) =>
    (await axios.post(`${API_BASE}/admin/upgrade-requests/${id}/approve`)).data,
  rejectUpgradeRequest: async (id, reason = null) =>
    (
      await axios.post(
        `${API_BASE}/admin/upgrade-requests/${id}/reject`,
        { reason },
        { headers: { 'Content-Type': 'application/json' } },
      )
    ).data,

  // Maintenance
  cleanupSearchHistory: async (dryRun = true) =>
    (
      await axios.post(
        `${API_BASE}/admin/maintenance/cleanup-search-history`,
        null,
        { params: { dry_run: dryRun }, timeout: 150000 },
      )
    ).data,
};

// AI Chart Analyzer (Pro)
export const chartAnalyzerAPI = {
  analyzeSymbol: async (symbol, period = '6mo') =>
    (
      await axios.post(
        `${API_BASE}/chart-analyzer/symbol`,
        { symbol, period },
        { headers: { 'Content-Type': 'application/json' } },
      )
    ).data,
  analyzeUpload: async (file, notes = null) => {
    const form = new FormData();
    form.append('file', file);
    if (notes) form.append('notes', notes);
    return (await axios.post(`${API_BASE}/chart-analyzer/upload`, form)).data;
  },
  history: async (limit = 10) =>
    (await axios.get(`${API_BASE}/chart-analyzer/history`, { params: { limit } })).data,
};

// Pro upgrade requests (Income Blueprint purchases)
export const upgradeAPI = {
  submit: async (orderEmail, note = null) =>
    (
      await axios.post(
        `${API_BASE}/upgrade-requests`,
        { order_email: orderEmail, note },
        { headers: { 'Content-Type': 'application/json' } },
      )
    ).data,
  me: async () => (await axios.get(`${API_BASE}/upgrade-requests/me`)).data,
};

// News API (Finnhub-powered)
export const newsAPI = {
  getMarketNews: async (category = 'general') => {
    const response = await axios.get(`${API_BASE}/news/market`, {
      params: { category },
      headers: authHeaders(),
    });
    return response.data;
  },
  getCompanyNews: async (symbol, days = 30) => {
    const response = await axios.get(`${API_BASE}/news/company/${symbol}`, {
      params: { days },
      headers: authHeaders(),
    });
    return response.data;
  },
  getSentiment: async (symbol) => {
    const response = await axios.get(`${API_BASE}/news/sentiment/${symbol}`, {
      headers: authHeaders(),
    });
    return response.data;
  },
};


// Alerts API (Pro only)
export const alertsAPI = {
  list: async () => {
    const r = await axios.get(`${API_BASE}/alerts`, { headers: authHeaders() });
    return r.data;
  },
  create: async (payload) => {
    const r = await axios.post(`${API_BASE}/alerts`, payload, {
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
    });
    return r.data;
  },
  history: async (limit = 20) => {
    const r = await axios.get(`${API_BASE}/alerts/history`, { params: { limit }, headers: authHeaders() });
    return r.data;
  },
  toggle: async (id, enabled) => {
    const r = await axios.patch(`${API_BASE}/alerts/${id}`, { enabled }, {
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
    });
    return r.data;
  },
  remove: async (id) => {
    const r = await axios.delete(`${API_BASE}/alerts/${id}`, { headers: authHeaders() });
    return r.data;
  },
  telegramStatus: async () => {
    const r = await axios.get(`${API_BASE}/alerts/telegram/status`, { headers: authHeaders() });
    return r.data;
  },
  telegramLink: async () => {
    const r = await axios.post(`${API_BASE}/alerts/telegram/link`, null, { headers: authHeaders() });
    return r.data;
  },
  telegramUnlink: async () => {
    const r = await axios.post(`${API_BASE}/alerts/telegram/unlink`, null, { headers: authHeaders() });
    return r.data;
  },
  telegramTest: async () => {
    const r = await axios.post(`${API_BASE}/alerts/telegram/test`, null, { headers: authHeaders() });
    return r.data;
  },
};

// AI Signals subscriptions (Pro)
export const signalsAPI = {
  categories: async () => (await axios.get(`${API_BASE}/signals/categories`)).data,
  getSubscription: async () => (await axios.get(`${API_BASE}/signals/subscription`, { headers: authHeaders() })).data,
  setSubscription: async (categories) => (await axios.put(`${API_BASE}/signals/subscription`, { categories }, {
    headers: { 'Content-Type': 'application/json', ...authHeaders() }
  })).data,
  recent: async (category, limit = 10) => (await axios.get(`${API_BASE}/signals/recent/${category}`, { params: { limit }, headers: authHeaders() })).data,
  runNow: async (category) => (await axios.post(`${API_BASE}/signals/run-now/${category}`, null, { headers: authHeaders() })).data,
};

// Paper Trading
export const paperAPI = {
  account: async () => (await axios.get(`${API_BASE}/paper/account`, { headers: authHeaders() })).data,
  trade: async (payload) => (await axios.post(`${API_BASE}/paper/trade`, payload, {
    headers: { 'Content-Type': 'application/json', ...authHeaders() }
  })).data,
  trades: async (limit = 50) => (await axios.get(`${API_BASE}/paper/trades`, { params: { limit }, headers: authHeaders() })).data,
  reset: async () => (await axios.post(`${API_BASE}/paper/reset`, null, { headers: authHeaders() })).data,
  leaderboard: async () => (await axios.get(`${API_BASE}/paper/leaderboard`)).data,
};

// Backtester
export const backtestAPI = {
  run: async (payload) => (await axios.post(`${API_BASE}/backtest/run`, payload, {
    headers: { 'Content-Type': 'application/json', ...authHeaders() }
  })).data,
};

// Market Intel
export const intelAPI = {
  earnings: async (days = 14) => (await axios.get(`${API_BASE}/intel/earnings`, { params: { days } })).data,
  insider: async (symbol, days = 90) => (await axios.get(`${API_BASE}/intel/insider/${symbol}`, { params: { days } })).data,
  ipos: async (days = 30) => (await axios.get(`${API_BASE}/intel/ipos`, { params: { days } })).data,
};

// Marketplace
export const marketplaceAPI = {
  list: async (sort = 'popular') => (await axios.get(`${API_BASE}/marketplace`, { params: { sort } })).data,
  publish: async (payload) => (await axios.post(`${API_BASE}/marketplace`, payload, {
    headers: { 'Content-Type': 'application/json', ...authHeaders() }
  })).data,
  copy: async (id) => (await axios.post(`${API_BASE}/marketplace/${id}/copy`, null, { headers: authHeaders() })).data,
  remove: async (id) => (await axios.delete(`${API_BASE}/marketplace/${id}`, { headers: authHeaders() })).data,
};

// AI Course Creator
export const coursesAPI = {
  quota: async () => (await axios.get(`${API_BASE}/courses/quota`)).data,
  generate: async (payload) =>
    (
      await axios.post(`${API_BASE}/courses/generate`, payload, {
        headers: { 'Content-Type': 'application/json' },
      })
    ).data,
  list: async () => (await axios.get(`${API_BASE}/courses`)).data,
  get: async (id) => (await axios.get(`${API_BASE}/courses/${id}`)).data,
  toggleLesson: async (courseId, lessonId, completed = undefined) => {
    const body = completed === undefined ? {} : { completed };
    return (
      await axios.post(
        `${API_BASE}/courses/${courseId}/lessons/${lessonId}/complete`,
        body,
        { headers: { 'Content-Type': 'application/json' } },
      )
    ).data;
  },
  remove: async (id) => (await axios.delete(`${API_BASE}/courses/${id}`)).data,
  share: async (id) =>
    (await axios.post(`${API_BASE}/courses/${id}/share`, {})).data,
  revokeShare: async (id) =>
    (await axios.delete(`${API_BASE}/courses/${id}/share`)).data,
  getShared: async (token) =>
    (await axios.get(`${API_BASE}/public/courses/${token}`)).data,
};

